import { isEmpty } from 'es-toolkit/compat'
import type {
    Game,
    GameSearchResponseType,
    GameSearchMatch,
    SearchResults,
    PaginatedResponse,
    GameSearchApiFilter,
} from '@game/types'
import { ref, useApi, useCoreConfig, useState } from '#imports'

type SearchOptions = {
    searchPhrase?: string
    filters?: GameSearchApiFilter
    page?: number
    page_size?: number
}

type GameSearchResponse = {
    success: boolean
    matches: any[]
    suggestions: any[]
}

const errorResponse: GameSearchResponse = {
    success: false,
    suggestions: [],
    matches: [],
}

export const useGameSearch = () => {
    const { site } = useCoreConfig()
    const api = useApi()

    const scrollToSearch = useState<boolean>('game:scrollToSearch', () => false)
    const filterToActivate = useState<string | undefined>('game:filterToActivate')

    const gameConfigurationApiUrl = `/gateway/game-configuration/public/game-configurations/vendor-provider-search`

    const vendor = () => {
        const isLoading = ref<boolean>(false)

        async function search(options: { vendors?: string[], providers?: string[] }): Promise<SearchResults | null> {
            isLoading.value = true

            try {
                if (!isEmpty(options)) {
                    const gameConfigResponse = await api<PaginatedResponse<Game[]>>(gameConfigurationApiUrl, {
                        method: 'POST',
                        body: {
                            ...(options.vendors?.length ? { vendors: options.vendors } : {}),
                            ...(options.providers?.length ? { providers: options.providers } : {}),
                            exclude_hidden_tile_types: false,
                            brand: site.brand,
                        },
                    })

                    isLoading.value = false

                    return {
                        success: !!gameConfigResponse.data.length,
                        matches: gameConfigResponse.data.length ? gameConfigResponse.data : [],
                        suggestions: [],
                    }
                }

                return errorResponse
            }
            catch {
                return errorResponse
            }
            finally {
                isLoading.value = false
            }
        }

        return {
            isLoading,
            search
        }
    }

    const loading = ref<boolean>(false)
    async function search(options: SearchOptions): Promise<SearchResults | null> {
        try {
            const { filters = {} } = options

            loading.value = true

            const gameConfigurationApiUrl = `/gateway/game-configuration/public/game-configurations/search`
            try {
                if (!isEmpty(filters)) {
                    // "Filter search" hits the game configuration search endpoint
                    filters.exclude_hidden_tile_types = false
                    const gameconfigurationResponse = await api<PaginatedResponse<Game[]>>(gameConfigurationApiUrl, {
                        method: 'POST',
                        params: {
                            page: options.page ?? 1,
                            page_size: options.page_size ?? 25,
                        },
                        body: {
                            filter: { ...filters },
                            brand: site.brand,
                            sort: 'KEEP_ORDER'
                        },
                    })

                    loading.value = false

                    return {
                        success: !!gameconfigurationResponse.data.length,
                        matches: gameconfigurationResponse.data.length ? gameconfigurationResponse.data : [],
                        suggestions: [],
                        meta: gameconfigurationResponse.meta,
                    }
                }
            }
            catch {
                return errorResponse
            }
            finally {
                loading.value = false
            }

            loading.value = true

            const searchPhrase = options.searchPhrase || ''
            const searchResponse = await api<GameSearchResponseType>(
                `/gateway/game-search/api/v1/public/game-search?search=${searchPhrase}&brand=${site.brand}`
            )

            // The search service returns an a list with objects with type: name || suggestion + the game slugs.
            // TODO: We need to return a list of games and not just their slugs.
            if (searchResponse.success) {
                const createGameSearchOptions = (slugs: string[]): { method: string, body: any } => {
                    return {
                        method: 'POST',
                        body: {
                            filter: { slugs: slugs, exclude_hidden_tile_types: false },
                            exclude_hidden_tile_typed: true,
                            brand: site.brand,
                            sort: 'KEEP_ORDER'
                        },
                    }
                }

                if ('matches' in searchResponse && searchResponse?.matches?.length) {
                    const gameConfigurationSearchResponse = await api<PaginatedResponse<Game[]>>(gameConfigurationApiUrl, createGameSearchOptions(searchResponse.matches.map((x: GameSearchMatch) => x.slug)))

                    return {
                        success: !!gameConfigurationSearchResponse.data.length,
                        matches: gameConfigurationSearchResponse.data.length ? gameConfigurationSearchResponse.data : [],
                        suggestions: [],
                    }
                }
            }

            return errorResponse
        }
        catch (err) {
            return errorResponse
        }
        finally {
            loading.value = false
        }
    }

    return {
        loading,
        scrollToSearch,
        filterToActivate,

        search,
        vendor
    }
}
